import {DiagramEntity, DiagramEntityType, DiagramTemplates} from "../../diagram/types/diagramEntity";
import {
    ClearDiagramParams,
    COLOR_NAME_TO_HEX,
    ConnectEntitiesParams,
    CreateEntityParams,
    ModifyConnectionParams,
    ModifyEntityParams,
    RemoveEntityParams,
    SHAPE_TO_TEMPLATE,
    ToolResult
} from "../types/chatTypes";
import {v4 as uuidv4} from 'uuid';
import log from 'loglevel';

const logger = log.getLogger('entityBridge');
logger.setLevel('debug');

// The 16 predefined toolbox colors
const TOOLBOX_COLORS = [
    "#222222", "#8b4513", "#006400", "#778899",  // Dark gray, Brown, Dark green, Slate gray
    "#4b0082", "#ff0000", "#ffa500", "#ffff00",  // Indigo, Red, Orange, Yellow
    "#00ff00", "#00ffff", "#0000ff", "#ff00ff",  // Green, Cyan, Blue, Magenta
    "#1e90ff", "#98fb98", "#ffe4b5", "#ff69b4"   // Dodger blue, Pale green, Moccasin, Pink
];

/**
 * Parse a hex color string to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

/**
 * Calculate color distance using simple Euclidean distance in RGB space
 */
function colorDistance(c1: { r: number; g: number; b: number }, c2: { r: number; g: number; b: number }): number {
    return Math.sqrt(
        Math.pow(c1.r - c2.r, 2) +
        Math.pow(c1.g - c2.g, 2) +
        Math.pow(c1.b - c2.b, 2)
    );
}

/**
 * Find the closest toolbox color to the given hex color
 */
function findClosestToolboxColor(hex: string): string {
    const inputRgb = hexToRgb(hex);
    if (!inputRgb) return '#0000ff'; // Default to blue if parsing fails

    let closestColor = TOOLBOX_COLORS[0];
    let minDistance = Infinity;

    for (const toolboxHex of TOOLBOX_COLORS) {
        const toolboxRgb = hexToRgb(toolboxHex);
        if (toolboxRgb) {
            const distance = colorDistance(inputRgb, toolboxRgb);
            if (distance < minDistance) {
                minDistance = distance;
                closestColor = toolboxHex;
            }
        }
    }

    if (closestColor !== hex.toLowerCase()) {
        logger.debug(`[resolveColor] Mapped ${hex} to closest toolbox color: ${closestColor}`);
    }

    return closestColor;
}

function resolveColor(color?: string): string {
    if (!color) return '#0000ff';
    const lower = color.toLowerCase().trim();

    // Check if it's a named color
    if (COLOR_NAME_TO_HEX[lower]) {
        const namedHex = COLOR_NAME_TO_HEX[lower];
        // Map named color to closest toolbox color
        return findClosestToolboxColor(namedHex);
    }

    // Check if it's a hex color
    if (color.startsWith('#')) {
        // If it's already a toolbox color, use it directly
        if (TOOLBOX_COLORS.includes(lower)) {
            return lower;
        }
        // Otherwise, find the closest toolbox color
        return findClosestToolboxColor(color);
    }

    // Default to blue
    return '#0000ff';
}

interface ResolvedEntity {
    id: string | null;
    label: string | null;
}

interface EntityInfo {
    id: string;
    label: string;
    template: string;
    color?: string;
}

/**
 * Get all entities currently in the diagram
 */
function getAllEntities(): Promise<EntityInfo[]> {
    return new Promise((resolve) => {
        const requestId = 'req-' + Date.now() + '-' + Math.random();

        const responseHandler = (e: CustomEvent) => {
            if (e.detail.requestId !== requestId) return;
            logger.debug('[getAllEntities] Got response:', e.detail.entities?.length, 'entities');
            document.removeEventListener('chatListEntitiesResponse', responseHandler as EventListener);
            resolve(e.detail.entities || []);
        };

        document.addEventListener('chatListEntitiesResponse', responseHandler as EventListener);

        const event = new CustomEvent('chatListEntities', {
            detail: { requestId },
            bubbles: true
        });
        document.dispatchEvent(event);

        setTimeout(() => {
            logger.warn('[getAllEntities] Timeout getting entities');
            document.removeEventListener('chatListEntitiesResponse', responseHandler as EventListener);
            resolve([]);
        }, 2000);
    });
}

/**
 * Calculate similarity between two strings (case-insensitive)
 * Returns a score between 0 and 1
 */
function stringSimilarity(str1: string, str2: string): number {
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();

    if (s1 === s2) return 1;
    if (s1.includes(s2) || s2.includes(s1)) return 0.8;

    // Simple Levenshtein-based similarity
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;

    if (longer.length === 0) return 1;

    // Count matching characters
    let matches = 0;
    for (let i = 0; i < shorter.length; i++) {
        if (longer.includes(shorter[i])) matches++;
    }

    return matches / longer.length;
}

/**
 * Infer the best matching entity from a partial or missing target
 * Uses fuzzy matching and context clues
 */
async function inferEntity(hint?: string): Promise<EntityInfo | null> {
    const entities = await getAllEntities();

    if (entities.length === 0) {
        logger.debug('[inferEntity] No entities in diagram');
        return null;
    }

    // If only one entity, use it
    if (entities.length === 1) {
        logger.debug('[inferEntity] Only one entity, using:', entities[0].label);
        return entities[0];
    }

    // If we have a hint, try to find the best match
    if (hint) {
        let bestMatch: EntityInfo | null = null;
        let bestScore = 0;

        for (const entity of entities) {
            const labelScore = stringSimilarity(hint, entity.label);
            const idScore = stringSimilarity(hint, entity.id);
            const score = Math.max(labelScore, idScore);

            if (score > bestScore && score > 0.3) { // Minimum threshold
                bestScore = score;
                bestMatch = entity;
            }
        }

        if (bestMatch) {
            logger.debug(`[inferEntity] Best match for "${hint}": ${bestMatch.label} (score: ${bestScore.toFixed(2)})`);
            return bestMatch;
        }
    }

    logger.debug('[inferEntity] Could not infer entity');
    return null;
}

/**
 * Resolve an entity label or ID to actual entity ID and label
 */
function resolveEntity(target: string): Promise<ResolvedEntity> {
    logger.debug('[resolveEntity] Resolving:', target);
    return new Promise((resolve) => {
        const requestId = 'req-' + Date.now() + '-' + Math.random();

        const responseHandler = (e: CustomEvent) => {
            if (e.detail.requestId !== requestId) return;
            logger.debug('[resolveEntity] Got response:', e.detail);
            document.removeEventListener('chatResolveEntityResponse', responseHandler as EventListener);
            resolve({
                id: e.detail.entityId,
                label: e.detail.entityLabel
            });
        };

        document.addEventListener('chatResolveEntityResponse', responseHandler as EventListener);

        const event = new CustomEvent('chatResolveEntity', {
            detail: {target, requestId},
            bubbles: true
        });
        document.dispatchEvent(event);

        setTimeout(() => {
            logger.warn('[resolveEntity] Timeout resolving:', target);
            document.removeEventListener('chatResolveEntityResponse', responseHandler as EventListener);
            resolve({id: null, label: null});
        }, 5000);
    });
}

export function createEntity(params: CreateEntityParams): ToolResult {
    logger.debug('[createEntity] Creating entity:', params);

    // Normalize params - some models use different parameter names
    const normalizedParams = { ...params } as CreateEntityParams & {
        x?: number;
        y?: number;
        z?: number;
        text?: string;
        name?: string;
    };

    // Handle position as array [x, y, z] instead of object {x, y, z}
    if (Array.isArray(normalizedParams.position)) {
        const posArray = normalizedParams.position as unknown as number[];
        normalizedParams.position = {
            x: posArray[0] ?? 0,
            y: posArray[1] ?? 1.5,
            z: posArray[2] ?? 2
        };
        logger.debug('[createEntity] Converted position array to object:', normalizedParams.position);
    }

    // Handle x, y, z directly instead of position object
    if (!normalizedParams.position && (normalizedParams.x !== undefined || normalizedParams.y !== undefined || normalizedParams.z !== undefined)) {
        normalizedParams.position = {
            x: normalizedParams.x ?? 0,
            y: normalizedParams.y ?? 1.5,
            z: normalizedParams.z ?? 2
        };
    }

    // Handle 'text' or 'name' as alias for 'label'
    if (normalizedParams.label === undefined && normalizedParams.text !== undefined) {
        normalizedParams.label = normalizedParams.text;
    }
    if (normalizedParams.label === undefined && normalizedParams.name !== undefined) {
        normalizedParams.label = normalizedParams.name;
    }

    const id = 'id' + uuidv4();
    const template = SHAPE_TO_TEMPLATE[normalizedParams.shape];
    const color = resolveColor(normalizedParams.color);
    const position = normalizedParams.position || {x: 0, y: 1.5, z: 2};

    const entity: DiagramEntity = {
        id,
        template,
        type: DiagramEntityType.ENTITY,
        color,
        text: normalizedParams.label || '',
        position,
        rotation: {x: 0, y: Math.PI, z: 0},
        scale: {x: 0.1, y: 0.1, z: 0.1},
    };

    logger.debug('[createEntity] Dispatching chatCreateEntity event:', entity);
    const event = new CustomEvent('chatCreateEntity', {
        detail: {entity},
        bubbles: true
    });
    document.dispatchEvent(event);

    const result = {
        toolName: 'create_entity',
        success: true,
        message: `Created ${normalizedParams.shape}${normalizedParams.label ? ` labeled "${normalizedParams.label}"` : ''} at position (${position.x.toFixed(1)}, ${position.y.toFixed(1)}, ${position.z.toFixed(1)})`,
        entityId: id
    };
    logger.debug('[createEntity] Done:', result);
    return result;
}

export async function connectEntities(params: ConnectEntitiesParams): Promise<ToolResult> {
    logger.debug('[connectEntities] Connecting:', params);
    // Resolve labels to actual entity IDs and get their labels
    const fromEntity = await resolveEntity(params.from);
    const toEntity = await resolveEntity(params.to);
    logger.debug('[connectEntities] Resolved from:', fromEntity, 'to:', toEntity);

    if (!fromEntity.id) {
        return {
            toolName: 'connect_entities',
            success: false,
            message: `Could not find entity "${params.from}"`
        };
    }

    if (!toEntity.id) {
        return {
            toolName: 'connect_entities',
            success: false,
            message: `Could not find entity "${params.to}"`
        };
    }

    const id = 'id' + uuidv4();
    const color = resolveColor(params.color);

    // Use explicit label if provided, otherwise generate default
    const fromLabel = fromEntity.label || params.from;
    const toLabel = toEntity.label || params.to;
    // If label is explicitly set (including empty string), use it; otherwise generate default
    const connectionLabel = params.label !== undefined ? params.label : `${fromLabel} to ${toLabel}`;

    const entity: DiagramEntity = {
        id,
        template: DiagramTemplates.CONNECTION,
        type: DiagramEntityType.ENTITY,
        color,
        text: connectionLabel,
        from: fromEntity.id,
        to: toEntity.id,
    };

    const event = new CustomEvent('chatCreateEntity', {
        detail: {entity},
        bubbles: true
    });
    document.dispatchEvent(event);

    const labelInfo = connectionLabel ? ` with label "${connectionLabel}"` : ' (no label)';
    return {
        toolName: 'connect_entities',
        success: true,
        message: `Connected "${fromLabel}" to "${toLabel}"${labelInfo}`,
        entityId: id
    };
}

export async function removeEntity(params: RemoveEntityParams): Promise<ToolResult> {
    // Normalize params - some models use different parameter names
    const normalizedParams = { ...params } as RemoveEntityParams & {
        entity?: string;
        name?: string;
    };

    // Handle 'entity' or 'name' as alias for 'target'
    let target = normalizedParams.target || normalizedParams.entity || normalizedParams.name;

    // If still no target, try to infer it
    if (!target) {
        const hint = normalizedParams.entity || normalizedParams.name;
        const inferredEntity = await inferEntity(hint);
        if (inferredEntity) {
            logger.info(`[removeEntity] Inferred target: "${inferredEntity.label}" (id: ${inferredEntity.id})`);
            target = inferredEntity.label || inferredEntity.id;
        }
    }

    if (!target) {
        return {
            toolName: 'remove_entity',
            success: false,
            message: `Error: No target entity specified. Please provide the entity name or ID to remove.`
        };
    }

    const event = new CustomEvent('chatRemoveEntity', {
        detail: { target },
        bubbles: true
    });
    document.dispatchEvent(event);

    return {
        toolName: 'remove_entity',
        success: true,
        message: `Removed entity "${target}"`
    };
}

// Convert degrees to radians
function degreesToRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
}

export async function modifyEntity(params: ModifyEntityParams): Promise<ToolResult> {
    // Normalize params - some models use different parameter names
    const normalizedParams = { ...params } as ModifyEntityParams & {
        entity?: string;
        name?: string;
        x?: number;
        y?: number;
        z?: number;
        text?: string;
        shape?: string;  // Some models include shape for context
    };

    // Handle 'entity' or 'name' as alias for 'target'
    if (!normalizedParams.target && normalizedParams.entity) {
        normalizedParams.target = normalizedParams.entity;
    }
    if (!normalizedParams.target && normalizedParams.name) {
        normalizedParams.target = normalizedParams.name;
    }

    // If still no target, try to infer it using color and shape hints
    if (!normalizedParams.target) {
        const entities = await getAllEntities();
        logger.debug('[modifyEntity] Trying to infer target from', entities.length, 'entities');

        // Try to find entity by color and/or shape
        if (normalizedParams.color || normalizedParams.shape) {
            const colorHint = normalizedParams.color?.toLowerCase();
            const shapeHint = normalizedParams.shape?.toLowerCase();

            for (const entity of entities) {
                const entityShape = entity.template?.replace('#', '').replace('-template', '').toLowerCase();
                const entityColor = entity.color?.toLowerCase();

                const matchesShape = !shapeHint || entityShape === shapeHint;

                // Match by actual entity color or by color name in label
                let matchesColor = false;
                if (colorHint) {
                    // Direct color match
                    if (entityColor === colorHint || entityColor === findClosestToolboxColor(colorHint)) {
                        matchesColor = true;
                    }
                    // Check if label contains color name
                    else if (
                        (colorHint === '#ff0000' && entity.label?.toLowerCase().includes('red')) ||
                        (colorHint === '#00ff00' && entity.label?.toLowerCase().includes('green')) ||
                        (colorHint === '#0000ff' && entity.label?.toLowerCase().includes('blue')) ||
                        (colorHint === '#ffff00' && entity.label?.toLowerCase().includes('yellow')) ||
                        (colorHint === '#ffa500' && entity.label?.toLowerCase().includes('orange')) ||
                        (colorHint === '#ff69b4' && entity.label?.toLowerCase().includes('pink')) ||
                        (colorHint === '#4b0082' && entity.label?.toLowerCase().includes('purple'))
                    ) {
                        matchesColor = true;
                    }
                }

                if (matchesShape && (matchesColor || (!colorHint && entities.length === 1))) {
                    logger.info(`[modifyEntity] Inferred target by shape/color: "${entity.label || entity.id}" (${entityShape}, ${entityColor})`);
                    normalizedParams.target = entity.label || entity.id;
                    break;
                }
            }
        }

        // Fall back to general inference
        if (!normalizedParams.target) {
            const hint = normalizedParams.entity || normalizedParams.name || undefined;
            const inferredEntity = await inferEntity(hint);
            if (inferredEntity) {
                logger.info(`[modifyEntity] Inferred target: "${inferredEntity.label}" (id: ${inferredEntity.id})`);
                normalizedParams.target = inferredEntity.label || inferredEntity.id;
            }
        }
    }

    // Handle position as array [x, y, z] instead of object {x, y, z}
    if (Array.isArray(normalizedParams.position)) {
        const posArray = normalizedParams.position as unknown as number[];
        normalizedParams.position = {
            x: posArray[0] ?? 0,
            y: posArray[1] ?? 1.5,
            z: posArray[2] ?? 0
        };
        logger.debug('[modifyEntity] Converted position array to object:', normalizedParams.position);
    }

    // Handle x, y, z directly instead of position object
    if (!normalizedParams.position && (normalizedParams.x !== undefined || normalizedParams.y !== undefined || normalizedParams.z !== undefined)) {
        normalizedParams.position = {
            x: normalizedParams.x ?? 0,
            y: normalizedParams.y ?? 1.5,
            z: normalizedParams.z ?? 0
        };
    }

    // Handle 'text' as alias for 'label'
    if (normalizedParams.label === undefined && normalizedParams.text !== undefined) {
        normalizedParams.label = normalizedParams.text;
    }

    // Validate target is provided
    if (!normalizedParams.target) {
        logger.error('[modifyEntity] Called without target! Params:', params);
        return {
            toolName: 'modify_entity',
            success: false,
            message: `Error: No target entity specified. Please provide the entity name or ID to modify.`
        };
    }

    const updates: Partial<DiagramEntity> = {};

    if (normalizedParams.color) {
        updates.color = resolveColor(normalizedParams.color);
    }
    if (normalizedParams.label !== undefined) {
        updates.text = normalizedParams.label;
    }
    if (normalizedParams.shape) {
        const template = SHAPE_TO_TEMPLATE[normalizedParams.shape as keyof typeof SHAPE_TO_TEMPLATE];
        if (template) {
            updates.template = template;
            logger.debug('[modifyEntity] Changing shape to:', normalizedParams.shape, '→', template);
        }
    }
    if (normalizedParams.position) {
        updates.position = normalizedParams.position;
    }
    if (normalizedParams.scale !== undefined) {
        // Accept either uniform scale (number) or 3D scale object
        if (typeof normalizedParams.scale === 'number') {
            updates.scale = {x: normalizedParams.scale, y: normalizedParams.scale, z: normalizedParams.scale};
        } else {
            updates.scale = normalizedParams.scale;
        }
    }
    if (normalizedParams.rotation !== undefined) {
        // Accept degrees from AI, convert to radians for internal use
        // Can be uniform (single number for Y rotation) or full 3D rotation
        if (typeof normalizedParams.rotation === 'number') {
            // Single number = Y-axis rotation (most common for "turn 90 degrees")
            updates.rotation = {x: 0, y: degreesToRadians(normalizedParams.rotation), z: 0};
        } else {
            updates.rotation = {
                x: degreesToRadians(normalizedParams.rotation.x),
                y: degreesToRadians(normalizedParams.rotation.y),
                z: degreesToRadians(normalizedParams.rotation.z)
            };
        }
    }

    const event = new CustomEvent('chatModifyEntity', {
        detail: {target: normalizedParams.target, updates},
        bubbles: true
    });
    document.dispatchEvent(event);

    const changes: string[] = [];
    if (normalizedParams.color) changes.push(`color to ${normalizedParams.color}`);
    if (normalizedParams.label !== undefined) changes.push(`label to "${normalizedParams.label}"`);
    if (normalizedParams.shape) changes.push(`shape to ${normalizedParams.shape}`);
    if (normalizedParams.position) changes.push(`position`);
    if (normalizedParams.scale !== undefined) {
        if (typeof normalizedParams.scale === 'number') {
            changes.push(`scale to ${normalizedParams.scale}`);
        } else {
            changes.push(`scale to (${normalizedParams.scale.x}, ${normalizedParams.scale.y}, ${normalizedParams.scale.z})`);
        }
    }
    if (normalizedParams.rotation !== undefined) {
        if (typeof normalizedParams.rotation === 'number') {
            changes.push(`rotation to ${normalizedParams.rotation}°`);
        } else {
            changes.push(`rotation to (${normalizedParams.rotation.x}°, ${normalizedParams.rotation.y}°, ${normalizedParams.rotation.z}°)`);
        }
    }

    return {
        toolName: 'modify_entity',
        success: true,
        message: `Modified entity "${normalizedParams.target}"${changes.length > 0 ? ': ' + changes.join(', ') : ''}`
    };
}

export async function modifyConnection(params: ModifyConnectionParams): Promise<ToolResult> {
    logger.debug('[modifyConnection] Modifying connection:', params);

    // Determine how to find the connection
    let connectionTarget: string | null = null;

    if (params.target) {
        // Direct target specified (connection label)
        connectionTarget = params.target;
    } else if (params.from && params.to) {
        // Find by from/to entities
        const fromEntity = await resolveEntity(params.from);
        const toEntity = await resolveEntity(params.to);

        if (!fromEntity.id) {
            return {
                toolName: 'modify_connection',
                success: false,
                message: `Could not find source entity "${params.from}"`
            };
        }

        if (!toEntity.id) {
            return {
                toolName: 'modify_connection',
                success: false,
                message: `Could not find destination entity "${params.to}"`
            };
        }

        // Use a special format to identify connection by from/to
        connectionTarget = `connection:${fromEntity.id}:${toEntity.id}`;
    } else {
        return {
            toolName: 'modify_connection',
            success: false,
            message: 'Must specify either "target" (connection label) or both "from" and "to" entities'
        };
    }

    const updates: Partial<DiagramEntity> = {};

    if (params.color) {
        updates.color = resolveColor(params.color);
    }
    if (params.label !== undefined) {
        updates.text = params.label;
    }

    const event = new CustomEvent('chatModifyConnection', {
        detail: {target: connectionTarget, updates},
        bubbles: true
    });
    document.dispatchEvent(event);

    const labelInfo = params.label ? ` with label "${params.label}"` : '';
    const colorInfo = params.color ? ` with color "${params.color}"` : '';

    return {
        toolName: 'modify_connection',
        success: true,
        message: `Modified connection${labelInfo}${colorInfo}`
    };
}

export function listEntities(): Promise<ToolResult> {
    logger.debug('[listEntities] Listing entities...');
    return new Promise((resolve) => {
        const responseHandler = (e: CustomEvent) => {
            document.removeEventListener('chatListEntitiesResponse', responseHandler as EventListener);
            const allItems = e.detail.entities as Array<{
                id: string;
                template: string;
                text: string;
                color?: string;
                position: { x: number; y: number; z: number };
                from?: string;
                to?: string;
            }>;
            logger.debug('[listEntities] Got response, items:', allItems.length);

            if (allItems.length === 0) {
                resolve({
                    toolName: 'list_entities',
                    success: true,
                    message: 'The diagram is empty.'
                });
                return;
            }

            // Separate entities from connections
            const entities = allItems.filter(e => !e.template.includes('connection'));
            const connections = allItems.filter(e => e.template.includes('connection'));

            // Build entity list
            const entityList = entities.map(e => {
                const shape = e.template.replace('#', '').replace('-template', '');
                return `- ${e.text || '(no label)'} (${shape}) at (${e.position?.x?.toFixed(1) || 0}, ${e.position?.y?.toFixed(1) || 0}, ${e.position?.z?.toFixed(1) || 0}) [id: ${e.id}]`;
            }).join('\n');

            // Build connection list with from/to info
            const connectionList = connections.map(c => {
                const fromEntity = entities.find(e => e.id === c.from);
                const toEntity = entities.find(e => e.id === c.to);
                const fromLabel = fromEntity?.text || c.from || '?';
                const toLabel = toEntity?.text || c.to || '?';
                return `- "${c.text || '(no label)'}" connects ${fromLabel} → ${toLabel} [id: ${c.id}]`;
            }).join('\n');

            let message = '';
            if (entities.length > 0) {
                message += `**Entities (${entities.length}):**\n${entityList}`;
            }
            if (connections.length > 0) {
                message += `\n\n**Connections (${connections.length}):**\n${connectionList}`;
                message += `\n\nTo modify a connection, use modify_connection with from/to entity names or the connection label.`;
            }

            resolve({
                toolName: 'list_entities',
                success: true,
                message: message || 'The diagram is empty.'
            });
        };

        document.addEventListener('chatListEntitiesResponse', responseHandler as EventListener);

        const event = new CustomEvent('chatListEntities', {bubbles: true});
        document.dispatchEvent(event);

        setTimeout(() => {
            logger.warn('[listEntities] Timeout waiting for response');
            document.removeEventListener('chatListEntitiesResponse', responseHandler as EventListener);
            resolve({
                toolName: 'list_entities',
                success: false,
                message: 'Failed to list entities (timeout)'
            });
        }, 5000);
    });
}

/**
 * Get all entities for session syncing (returns raw entity data)
 */
export function getEntitiesForSync(): Promise<Array<{
    id: string;
    template: string;
    text?: string;
    color?: string;
    position?: { x: number; y: number; z: number };
}>> {
    return new Promise((resolve) => {
        const responseHandler = (e: CustomEvent) => {
            document.removeEventListener('chatListEntitiesResponse', responseHandler as EventListener);
            resolve(e.detail.entities || []);
        };

        document.addEventListener('chatListEntitiesResponse', responseHandler as EventListener);

        const event = new CustomEvent('chatListEntities', {bubbles: true});
        document.dispatchEvent(event);

        setTimeout(() => {
            document.removeEventListener('chatListEntitiesResponse', responseHandler as EventListener);
            resolve([]);
        }, 5000);
    });
}

/**
 * Clear all entities from the diagram
 */
export async function clearDiagram(params: ClearDiagramParams): Promise<ToolResult> {
    if (!params.confirmed) {
        return {
            toolName: 'clear_diagram',
            success: false,
            message: 'Clearing the diagram requires confirmation. Please ask the user to confirm before proceeding.'
        };
    }

    // Get all entities first
    const entities = await getEntitiesForSync();

    if (entities.length === 0) {
        return {
            toolName: 'clear_diagram',
            success: true,
            message: 'The diagram is already empty.'
        };
    }

    // Dispatch clear event to remove all entities at once
    const event = new CustomEvent('chatClearDiagram', {
        bubbles: true
    });
    document.dispatchEvent(event);

    return {
        toolName: 'clear_diagram',
        success: true,
        message: `Cleared ${entities.length} entities from the diagram.`
    };
}

/**
 * Get camera data for session sync (returns raw camera data)
 */
export function getCameraForSync(): Promise<{
    position: { x: number; y: number; z: number };
    forward: { x: number; y: number; z: number };
    groundForward: { x: number; z: number };
    groundRight: { x: number; z: number };
} | null> {
    return new Promise((resolve) => {
        const responseHandler = (e: CustomEvent) => {
            document.removeEventListener('chatGetCameraResponse', responseHandler as EventListener);
            resolve(e.detail);
        };

        document.addEventListener('chatGetCameraResponse', responseHandler as EventListener);

        const event = new CustomEvent('chatGetCamera', {bubbles: true});
        document.dispatchEvent(event);

        setTimeout(() => {
            document.removeEventListener('chatGetCameraResponse', responseHandler as EventListener);
            resolve(null);
        }, 2000);
    });
}

/**
 * Get the current camera position and orientation
 * Returns world-space coordinates with ground-projected directions for intuitive placement
 */
export function getCameraPosition(): Promise<ToolResult> {
    logger.debug('[getCameraPosition] Getting camera position...');
    return new Promise((resolve) => {
        const responseHandler = (e: CustomEvent) => {
            document.removeEventListener('chatGetCameraResponse', responseHandler as EventListener);
            const {position, forward, groundForward, groundRight} = e.detail;
            logger.debug('[getCameraPosition] Got response:', e.detail);

            // Compute example positions for each direction
            const distance = 2;
            const frontPos = {
                x: position.x + groundForward.x * distance,
                y: position.y,
                z: position.z + groundForward.z * distance
            };
            const rightPos = {
                x: position.x + groundRight.x * distance,
                y: position.y,
                z: position.z + groundRight.z * distance
            };
            const leftPos = {
                x: position.x - groundRight.x * distance,
                y: position.y,
                z: position.z - groundRight.z * distance
            };
            const backPos = {
                x: position.x - groundForward.x * distance,
                y: position.y,
                z: position.z - groundForward.z * distance
            };

            const message = `User's current view (world coordinates):

Position: (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)})
Looking direction: (${forward.x.toFixed(2)}, ${forward.y.toFixed(2)}, ${forward.z.toFixed(2)})

Ground-plane directions (use these for left/right/forward/back):
• Forward: (${groundForward.x.toFixed(2)}, 0, ${groundForward.z.toFixed(2)})
• Right: (${groundRight.x.toFixed(2)}, 0, ${groundRight.z.toFixed(2)})

To place entities relative to user (${distance}m away, at eye level y=${position.y.toFixed(1)}):
• FORWARD: (${frontPos.x.toFixed(2)}, ${frontPos.y.toFixed(2)}, ${frontPos.z.toFixed(2)})
• BACK: (${backPos.x.toFixed(2)}, ${backPos.y.toFixed(2)}, ${backPos.z.toFixed(2)})
• RIGHT: (${rightPos.x.toFixed(2)}, ${rightPos.y.toFixed(2)}, ${rightPos.z.toFixed(2)})
• LEFT: (${leftPos.x.toFixed(2)}, ${leftPos.y.toFixed(2)}, ${leftPos.z.toFixed(2)})

Formula: position + (groundForward * distance) for forward, position + (groundRight * distance) for right, etc.`;

            resolve({
                toolName: 'get_camera_position',
                success: true,
                message
            });
        };

        document.addEventListener('chatGetCameraResponse', responseHandler as EventListener);

        const event = new CustomEvent('chatGetCamera', {bubbles: true});
        document.dispatchEvent(event);

        setTimeout(() => {
            logger.warn('[getCameraPosition] Timeout waiting for response');
            document.removeEventListener('chatGetCameraResponse', responseHandler as EventListener);
            resolve({
                toolName: 'get_camera_position',
                success: false,
                message: 'Failed to get camera position (timeout)'
            });
        }, 5000);
    });
}
